// src/pages/Platform/PlatformLanguages.js
//
// Plain CRUD for the languages MarketingContent.js's own editor and
// posflect.com's own MarketingLanguageSwitcher.js can translate into — same
// field shape as the tenant Languages.js page (name/code/direction/
// is_active/is_default/sort_order), but built with Companies.js's own
// plain-markup style rather than that page's Tailwind+DataTable.jsx
// components (see Companies.js's own doc comment on why the Platform tree
// can't reuse those). Gated by "marketingcontent_manage" — one permission
// for both this screen and MarketingContent.js (see seed_platform_admin.go's
// own doc comment).
import { useEffect, useState, useContext } from "react";
import {
  getPlatformLanguages,
  createPlatformLanguage,
  updatePlatformLanguage,
  deletePlatformLanguage,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

const emptyForm = { name: "", code: "", direction: "ltr", is_active: true, is_default: false, sort_order: 0 };

export default function PlatformLanguages() {
  const { token } = useContext(PlatformAuthContext);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPlatformLanguages(token);
      setLanguages(data || []);
    } catch (err) {
      console.error("Failed to load languages:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      code: row.code,
      direction: row.direction || "ltr",
      is_active: !!row.is_active,
      is_default: !!row.is_default,
      sort_order: row.sort_order || 0,
    });
    setError("");
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      setError("Name and code are required");
      return;
    }
    setSaving(true);
    setError("");
    const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editingId) {
        await updatePlatformLanguage(token, editingId, payload);
      } else {
        await createPlatformLanguage(token, payload);
      }
      setFormOpen(false);
      load();
    } catch (err) {
      setError(err.data?.error || err.message || "Failed to save language");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? This fails if it's the default language or still has saved translations.`)) return;
    try {
      await deletePlatformLanguage(token, row.id);
      load();
    } catch (err) {
      alert(err.data?.error || err.message || "Failed to delete language");
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Marketing site languages</h2>
        <button style={primaryBtnStyle} onClick={openCreate}>+ Add language</button>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              {["Name", "Code", "Direction", "Status", "Order", ""].map((h, i) => (
                <th key={i} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={tdEmptyStyle}>Loading...</td></tr>
            ) : languages.length === 0 ? (
              <tr><td colSpan={6} style={tdEmptyStyle}>No languages yet.</td></tr>
            ) : (
              languages.map((l) => (
                <tr key={l.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    {l.name}
                    {l.is_default && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#2563eb" }}>DEFAULT</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: "monospace" }}>{l.code}</td>
                  <td style={tdStyle}>{l.direction === "rtl" ? "RTL" : "LTR"}</td>
                  <td style={tdStyle}>{l.is_active ? "Active" : "Disabled"}</td>
                  <td style={tdStyle}>{l.sort_order}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <button style={linkBtnStyle} onClick={() => openEdit(l)}>Edit</button>
                    <button style={linkBtnStyle} onClick={() => handleDelete(l)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <form onSubmit={handleSubmit}>
              <h3 style={{ marginTop: 0 }}>{editingId ? "Edit language" : "Add language"}</h3>

              <label style={labelStyle}>Name</label>
              <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

              <label style={labelStyle}>Code (e.g. en, ar, fr)</label>
              <input
                style={inputStyle}
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.trim().toLowerCase() })}
                required
              />

              <label style={labelStyle}>Text direction</label>
              <select style={inputStyle} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                <option value="ltr">LTR — Left to Right</option>
                <option value="rtl">RTL — Right to Left</option>
              </select>

              <label style={labelStyle}>Sort order</label>
              <input
                style={inputStyle}
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              />

              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active (selectable on the marketing site)
              </label>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                />
                Default language (English fallback)
              </label>

              {error && <div style={errorBoxStyle}>{error}</div>}

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button type="button" style={secondaryBtnStyle} onClick={() => setFormOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" style={primaryBtnStyle} disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Add language"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase" };
const tdStyle = { padding: "14px 16px", fontSize: 14, color: "#334155", verticalAlign: "top" };
const tdEmptyStyle = { padding: 40, textAlign: "center", color: "#94a3b8" };
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box", marginBottom: 4 };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 16px", fontSize: 14, cursor: "pointer" };
const linkBtnStyle = { background: "none", border: "none", color: "#2563eb", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: 10 };
const errorBoxStyle = { background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 12 };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: 420, maxWidth: "90vw", maxHeight: "90vh", overflowY: "auto" };
